/**
 * Claim rules in the browser: the shapes /api/rules returns (api/shared/rules.ts), how a
 * rule matches a payer, and the Rules page's preview of which claims a biller rule
 * would catch. docs/RULES.md.
 *
 * The preview is a preview: it counts the claims in Claim detail that a rule matches.
 * Nothing here moves a claim to another department or changes a count elsewhere in the
 * app — applying the rules is a later change, and reassigning coinsurance claims to
 * billers is the pipeline's job.
 */
import type { ClaimDetailData } from '@/data/claimDetail'
import { billedBasis } from '@/data/billedBasis'

export type RuleTable = 'coinsurance_biller_rule' | 'coinsurance_payer' | 'hard_payer' | 'payer_type_setting'
export type MatchKind = 'exact' | 'pattern'

interface Stamped {
  /** In force from / until, by the claim's bill date ('YYYY-MM-DD', inclusive); null = no limit. */
  effectiveFrom: string | null
  effectiveTo: string | null
  id: number
  active: boolean
  createdBy: string
  createdAt: string
  updatedBy: string | null
  updatedAt: string | null
}

export interface BillerRule extends Stamped {
  matchField: 'payer_type' | 'payer'
  matchKind: MatchKind
  matchValue: string
  creditedDepartment: string
  priority: number
  note: string | null
}

export interface CoinsurancePayer extends Stamped {
  payerName: string
  state: string | null
  weight: number
  includeForHmo: boolean
  includeForMedicare: boolean
  note: string | null
}

export interface HardPayer extends Stamped {
  matchKind: MatchKind
  payerName: string
  /** Facility state; null = every state. A claim's own state's rule wins. */
  state: string | null
  /** What one claim counts as: 1 = normal, 1.5 = one and a half (Shayna #163). */
  weight: number
  reason: string | null
}

/**
 * A payer type's setting (sql/cpc_rules_006_payer_type_setting.sql). The one so far is
 * `zero_touch_max_age_days`: the 0% touch age limit (MEASURE-RULES Rule 5.7).
 */
export interface PayerTypeSetting extends Stamped {
  payerType: string
  setting: 'zero_touch_max_age_days'
  value: number
  reason: string | null
}

export interface RuleSet {
  biller: BillerRule[]
  payers: CoinsurancePayer[]
  hard: HardPayer[]
  /** Absent from an API that predates #695. */
  payerTypes?: PayerTypeSetting[]
}

export interface AuditEntry {
  id: number
  table: RuleTable
  rowId: number
  action: 'add' | 'edit'
  field: string | null
  oldValue: string | null
  newValue: string | null
  source: 'page' | 'import'
  actor: string
  at: string
}

export const TABLE_LABEL: Record<RuleTable, string> = {
  coinsurance_biller_rule: 'Coinsurance biller',
  coinsurance_payer: 'Coinsurance payer',
  hard_payer: 'Hard payer',
  payer_type_setting: 'Payer type',
}

/* ------------------------------------------------------------------ matching */

const norm = (s: string) => s.trim().toLowerCase()

/**
 * Does `value` match? Exact is whole-string, ignoring case and outer spaces. A pattern
 * uses `*` for any run of characters, also whole-string: `*coins*` finds "Medigap
 * Coins", `HMO*` finds "HMO/Private Insurance" but not "Private HMO Coins".
 */
export function matchesValue(kind: MatchKind, rule: string, value: string | null | undefined): boolean {
  if (value === null || value === undefined) return false
  if (kind === 'exact') return norm(rule) === norm(value)
  const re = new RegExp(
    '^' + norm(rule).split('*').map((p) => p.replace(/[.+?^${}()|[\]\\]/g, '\\$&')).join('.*') + '$',
    's',
  )
  return re.test(norm(value))
}

/** Whether the text looks like a pattern (holds a `*`). */
export const isPattern = (s: string) => s.includes('*')

/** The first active biller rule, by priority then id, that matches this payer. */
export function firstBillerRule(
  rules: readonly BillerRule[],
  payerType: string | null,
  payer: string | null,
): BillerRule | null {
  for (const r of sortBiller(rules)) {
    if (!r.active) continue
    if (matchesValue(r.matchKind, r.matchValue, r.matchField === 'payer_type' ? payerType : payer)) return r
  }
  return null
}

export const sortBiller = (rules: readonly BillerRule[]) =>
  [...rules].sort((a, b) => a.priority - b.priority || a.id - b.id)

/* ------------------------------------------------------------------ the payers in the data */

/**
 * One payer type × payer combination in Claim detail, with its claims by department.
 * Coinsurance claims and the rest of the same pair are separate combos.
 */
export interface PayerCombo {
  payerType: string
  payer: string
  claims: number
  byDept: Record<string, number>
  /** Coinsurance claims (isCoinsuranceClaim). Absent: judged by the payer's name. */
  coinsurance?: boolean
}

/**
 * Claim detail's rows grouped by payer type and payer, with each group's claims by the
 * department they are billed under today. ~400K rows become a few thousand groups, so
 * every preview after this is cheap.
 */
export function payerCombos(data: ClaimDetailData): PayerCombo[] {
  const { dict, cols, n } = data
  const by = new Map<string, PayerCombo>()
  for (let i = 0; i < n; i++) {
    // Billed claims only: a resolved-basis-only row is in no billed count (review #397).
    if (!billedBasis(cols, i)) continue
    const p = cols.p[i]!
    const pn = cols.pn[i]!
    const ty = cols.ty?.[i]
    const coinsurance = isCoinsuranceClaim(ty === undefined ? null : dict.ty?.[ty], dict.pn[pn] ?? '')
    const key = `${p}|${pn}|${coinsurance ? 1 : 0}`
    let c = by.get(key)
    if (!c) {
      c = { payerType: dict.p[p] ?? '', payer: dict.pn[pn] ?? '', claims: 0, byDept: {}, coinsurance }
      by.set(key, c)
    }
    c.claims++
    const d = dict.dept[cols.dept[i]!] ?? ''
    c.byDept[d] = (c.byDept[d] ?? 0) + 1
  }
  return [...by.values()].sort((a, b) => b.claims - a.claims)
}

/** Claims whose payer name a payer-name rule (exact or pattern) matches. */
export function claimsMatching(combos: readonly PayerCombo[], kind: MatchKind, value: string): number {
  let n = 0
  for (const c of combos) if (matchesValue(kind, value, c.payer)) n += c.claims
  return n
}

/** Claims a biller rule matches on its own, ignoring the rules above it. */
export function claimsForBillerRule(combos: readonly PayerCombo[], r: BillerRule): number {
  let n = 0
  for (const c of combos) {
    if (matchesValue(r.matchKind, r.matchValue, r.matchField === 'payer_type' ? c.payerType : c.payer)) n += c.claims
  }
  return n
}

/** Coinsurance as the data names it: a payer name containing "coins". */
export const looksLikeCoinsurance = (payer: string) => /coins/i.test(payer)

/**
 * A coinsurance claim: claim type Co-Insurance, or a payer name containing "coins".
 * Claim detail has each claim's own payer, not its primary payer (docs/RULES.md), so
 * this is the best the browser can tell.
 */
export const isCoinsuranceClaim = (claimType: string | null | undefined, payer: string) =>
  /co-?\s?ins/i.test(claimType ?? '') || looksLikeCoinsurance(payer)

const comboIsCoinsurance = (c: PayerCombo) => c.coinsurance ?? looksLikeCoinsurance(c.payer)

export interface BillerPreview {
  rule: BillerRule
  claims: number
  /** Of those, already billed under the credited department. */
  alreadyThere: number
  /** The rest, by the department they are billed under today: what would move, and from where. */
  from: Record<string, number>
  combos: PayerCombo[]
}

/**
 * For each active biller rule, the claims it would catch — first match wins, by
 * priority, so a lower rule only sees what the rules above it left.
 */
export function previewBiller(
  rules: readonly BillerRule[],
  combos: readonly PayerCombo[],
  coinsuranceOnly: boolean,
): { byRule: BillerPreview[]; unmatched: number; considered: number } {
  // The preview is of today: rules in force today, by their dates.
  const day = todayYmd()
  const active = sortBiller(rules).filter((r) => r.active && inForceOn(r, day))
  const out = new Map<number, BillerPreview>(
    active.map((r) => [r.id, { rule: r, claims: 0, alreadyThere: 0, from: {}, combos: [] }]),
  )
  let unmatched = 0
  let considered = 0
  for (const c of combos) {
    if (coinsuranceOnly && !comboIsCoinsurance(c)) continue
    considered += c.claims
    const r = firstBillerRule(active, c.payerType, c.payer)
    if (!r) {
      unmatched += c.claims
      continue
    }
    const p = out.get(r.id)!
    p.claims += c.claims
    // Credited by the primary payer: where they go is the model's call, so none "moves" here.
    if (!isPrimaryCredit(r.creditedDepartment)) {
      for (const [d, k] of Object.entries(c.byDept)) {
        if (norm(d) === norm(r.creditedDepartment)) p.alreadyThere += k
        else p.from[d] = (p.from[d] ?? 0) + k
      }
    }
    p.combos.push(c)
  }
  return { byRule: [...out.values()], unmatched, considered }
}

/* ------------------------------------------------------------------ the Rules page's lists */

/** One payer's claims, summed over its payer types. */
export interface PayerTotal {
  payer: string
  claims: number
  /** Of those, coinsurance claims. */
  coinsurance: number
  /** Coinsurance claims by the department they are billed under today. */
  coinsByDept: Record<string, number>
  /** Every payer type (group) the payer's claims carry, A–Z. For the payer type filter. */
  types: string[]
}

/** Every payer in the data with its claims, most claims first. */
export function payerTotals(combos: readonly PayerCombo[]): PayerTotal[] {
  const by = new Map<string, PayerTotal>()
  for (const c of combos) {
    // One payer however the data spells it ("HUMANA MLTC", "Humana MLTC "): rules match
    // names ignoring case and outer spaces, so the page lists it once (#399).
    let t = by.get(norm(c.payer))
    if (!t) by.set(norm(c.payer), (t = { payer: c.payer.trim(), claims: 0, coinsurance: 0, coinsByDept: {}, types: [] }))
    t.claims += c.claims
    if (c.payerType && !t.types.includes(c.payerType)) t.types = [...t.types, c.payerType].sort()
    if (comboIsCoinsurance(c)) {
      t.coinsurance += c.claims
      for (const [d, k] of Object.entries(c.byDept)) t.coinsByDept[d] = (t.coinsByDept[d] ?? 0) + k
    }
  }
  return [...by.values()].sort((a, b) => b.claims - a.claims || a.payer.localeCompare(b.payer))
}

/**
 * One payer's (or one state's) saved settings, as the page shows them (#597, Yehuda
 * 2026-10-14): `head` is what is in force when the data starts, the latest setting
 * starting on or before `dataStart` (the undated one counts as earliest), shown as the
 * payer's own line with its weight; `later` are the changes from a date after the data
 * starts, shown under it; `history` are the settings `head` replaced before the data
 * starts, so they apply to no claim in the data, folded away. Until then the undated
 * line was the headline, showing a grey "Default 1" when the weight set was a "From Jan
 * 1, 2026" line under it, which read as "every hard payer is 1".
 *
 * `rows` are the active rows for one payer and state. With no `dataStart` (the bundle
 * not loaded), the undated row is the head and every dated row is later.
 */
export function splitAtDataStart<T extends { effectiveFrom: string | null }>(
  rows: readonly T[],
  dataStart: string | null,
): { head: T | null; later: T[]; history: T[] } {
  const byFrom = [...rows].sort((a, b) => (a.effectiveFrom ?? '').localeCompare(b.effectiveFrom ?? ''))
  if (!dataStart) {
    return { head: byFrom.find((r) => !r.effectiveFrom) ?? null, later: byFrom.filter((r) => r.effectiveFrom), history: [] }
  }
  const before = byFrom.filter((r) => !r.effectiveFrom || r.effectiveFrom <= dataStart)
  return {
    head: before[before.length - 1] ?? null,
    later: byFrom.filter((r) => r.effectiveFrom && r.effectiveFrom > dataStart),
    history: before.slice(0, -1).reverse(),
  }
}

/** The first day of the data: the earliest month in the bundle ('YYYY-MM' → 'YYYY-MM-01'), or null. */
export function dataStartOf(months: readonly string[] | null | undefined): string | null {
  const first = [...(months ?? [])].filter((m) => /^\d{4}-\d{2}$/.test(m)).sort()[0]
  return first ? `${first}-01` : null
}

/** A payer's billed claims on one bill date at one facility state. */
interface PayerDay {
  /** Facility state, upper case; '' when unknown. */
  state: string
  /** Bill date, 'YYYY-MM-DD'. */
  day: string
  claims: number
  coinsurance: number
}

/** Billed claims by payer (as rules match it: no case, no outer spaces), facility state and bill date. */
export type PayerDays = Map<string, PayerDay[]>

/**
 * Claim detail's billed claims grouped by payer, facility state and bill date, so every
 * line on the payer tabs can count its own claims: a state's line those at that state's
 * facilities, a dated line those billed on or after its date (Shayna #597: "listing the
 * number of claims is helpful"). Same claims as `payerCombos`.
 */
export function payerDays(data: ClaimDetailData): PayerDays {
  const { dict, cols, n } = data
  const by = new Map<string, Map<string, PayerDay>>()
  for (let i = 0; i < n; i++) {
    if (!billedBasis(cols, i)) continue
    const payer = norm(dict.pn[cols.pn[i]!] ?? '')
    const si = cols.s?.[i]
    const di = cols.d?.[i]
    const state = (si === undefined ? '' : (dict.s?.[si] ?? '')).trim().toUpperCase()
    const day = di === undefined ? '' : (dict.d?.[di] ?? '')
    const ty = cols.ty?.[i]
    const coins = isCoinsuranceClaim(ty === undefined ? null : dict.ty?.[ty], dict.pn[cols.pn[i]!] ?? '')
    let m = by.get(payer)
    if (!m) by.set(payer, (m = new Map()))
    const k = `${state}|${day}`
    let g = m.get(k)
    if (!g) m.set(k, (g = { state, day, claims: 0, coinsurance: 0 }))
    g.claims++
    if (coins) g.coinsurance++
  }
  return new Map([...by].map(([p, m]) => [p, [...m.values()]]))
}

/**
 * The claims one line on a payer tab applies to: the payer's (every name a pattern
 * matches), at facilities in `state` when it is one state's line, billed on or after
 * `from` when it is a dated change. 0 when none. `coinsurance`: count coinsurance
 * claims only (the Coinsurance payers tab).
 */
export function lineClaims(
  days: PayerDays,
  line: { kind: MatchKind; payer: string; state: string | null; from: string | null },
  coinsurance = false,
): number {
  const groups =
    line.kind === 'exact'
      ? (days.get(norm(line.payer)) ?? [])
      : [...days].filter(([p]) => matchesValue('pattern', line.payer, p)).flatMap(([, g]) => g)
  const st = line.state?.trim().toUpperCase() ?? null
  let n = 0
  for (const g of groups) {
    if (st && g.state !== st) continue
    if (line.from && g.day < line.from) continue
    n += coinsurance ? g.coinsurance : g.claims
  }
  return n
}

/**
 * New priorities for the active biller rules after moving one up or down: 10, 20, 30…
 * in the new order. Only the rules whose number changes, so each save is one audited
 * edit per rule that actually moved. The page never shows the numbers.
 */
export function reorderBiller(
  rules: readonly BillerRule[],
  id: number,
  dir: -1 | 1,
): { id: number; priority: number }[] {
  const order = sortBiller(rules).filter((r) => r.active)
  const i = order.findIndex((r) => r.id === id)
  const j = i + dir
  if (i < 0 || j < 0 || j >= order.length) return []
  ;[order[i], order[j]] = [order[j]!, order[i]!]
  return order
    .map((r, k) => ({ id: r.id, priority: (k + 1) * 10, was: r.priority }))
    .filter((x) => x.priority !== x.was)
    .map(({ id, priority }) => ({ id, priority }))
}

/**
 * A biller rule's "credited to" that is not a department: the coinsurance goes to the
 * department of the same visit's PRIMARY claim (Yehuda, 2026-10-06, for Medicaid
 * coinsurance). Only the model can resolve it, since it alone has the primary claim
 * (docs/RULES.md); the page previews which claims the rule catches, not where they go.
 */
export const PRIMARY_PAYER = '@primary'

export const isPrimaryCredit = (dept: string) => dept.trim().toLowerCase() === PRIMARY_PAYER

/** How the page names a rule's "credited to". */
export const creditLabel = (dept: string) => (isPrimaryCredit(dept) ? 'the primary payer’s department' : dept)

/** A biller rule as the page offers it before it is saved. */
export interface BillerSentence {
  matchField: BillerRule['matchField']
  matchValue: string
  creditedDepartment: string
  /** Optional: in force from / until, by bill date. */
  effectiveFrom?: string | null
  effectiveTo?: string | null
}

/** In force on a day ('YYYY-MM-DD'): no limit where a date is blank. */
export const inForceOn = (r: { effectiveFrom?: string | null; effectiveTo?: string | null }, day: string) =>
  (!r.effectiveFrom || r.effectiveFrom <= day) && (!r.effectiveTo || day <= r.effectiveTo)

export const todayYmd = () => new Date().toISOString().slice(0, 10)

/** The bill dates a rule may start or end on (api/shared/rules.ts says the same). */
export const MIN_DAY = '2000-01-01'
export const MAX_DAY = '2099-12-31'

/**
 * A whole date in range, 'YYYY-MM-DD'. A date field reports a year typed half-way as a
 * real date ("0002-01-01" after typing 2), so nothing is saved until this is true (#399).
 */
export function isBillDay(s: string | null | undefined): s is string {
  if (!s || !/^\d{4}-\d{2}-\d{2}$/.test(s) || s < MIN_DAY || s > MAX_DAY) return false
  const d = new Date(`${s}T00:00:00Z`)
  return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === s
}

/** A coinsurance or hard payer row, as far as "what is in force" needs it. */
type PayerSetting = { payerName: string; state: string | null; active: boolean; effectiveFrom: string | null; effectiveTo: string | null }

/**
 * The saved row a payer (and state) counts by on the day before `from`: of its active
 * rows in force then, the one that started last; for a state with none, the all-states
 * row. Null: nothing set, the payer counts by the defaults. `same` narrows the rows
 * further (a hard payer's match kind). What a new "Change from a date…" line starts as,
 * and what a saved dated line is compared with (#584).
 */
export function settingBefore<T extends PayerSetting>(
  rows: readonly T[],
  payer: string,
  state: string | null,
  from: string,
  same: (r: T) => boolean = () => true,
): T | null {
  const low = (s: string | null | undefined) => (s ?? '').trim().toLowerCase()
  const of = (st: string | null) =>
    rows
      .filter(
        (r) =>
          r.active &&
          same(r) &&
          low(r.payerName) === low(payer) &&
          low(r.state) === low(st) &&
          (!r.effectiveFrom || r.effectiveFrom < from) &&
          (!r.effectiveTo || r.effectiveTo >= from),
      )
      .sort((a, b) => (b.effectiveFrom ?? '').localeCompare(a.effectiveFrom ?? ''))[0]
  return of(state) ?? (state ? of(null) : undefined) ?? null
}

/**
 * True when a saved dated line says what was already in force before its date, so it
 * changes no claim (#584: "Private HMO Coins" at 0.5, and again from Jan 1, 2026 at 0.5).
 * `fields` are the values that decide how a claim counts; `defaults` stand in when
 * nothing was set before. A line with an end date is never "no change": it ends one.
 */
export function changesNothing<T extends PayerSetting>(
  rows: readonly T[],
  row: T,
  fields: readonly (keyof T)[],
  defaults: Partial<T>,
  same?: (r: T) => boolean,
): boolean {
  if (!row.active || !row.effectiveFrom || row.effectiveTo) return false
  const before = settingBefore(rows, row.payerName, row.state, row.effectiveFrom, same)
  return fields.every((f) => (before ? before[f] : defaults[f]) === row[f])
}

/** What an import or bulk edit did (api/shared/rules.ts, importRows). */
export interface ImportResult {
  added: number
  updated: number
  unchanged: number
  /** The same rule more than once in the request: saved once. */
  repeated?: number
}

/** Adds up the results of a bulk edit sent in several requests. */
export const addResults = (a: ImportResult, b: ImportResult): ImportResult => ({
  added: a.added + b.added,
  updated: a.updated + b.updated,
  unchanged: a.unchanged + b.unchanged,
  repeated: (a.repeated ?? 0) + (b.repeated ?? 0),
})

/** "3 added, 2 changed. 12 already set, skipped." What a bulk edit or import did, in words. */
export function importSummary(r: ImportResult): string {
  const saved = [r.added && `${r.added} added`, r.updated && `${r.updated} changed`].filter(Boolean).join(', ')
  const skipped = r.unchanged + (r.repeated ?? 0)
  const skip = skipped ? `${skipped} already set, skipped` : ''
  if (!saved) return skip ? `Nothing to change: ${skip.replace(', skipped', '')}.` : 'Nothing to change.'
  return skip ? `${saved}. ${skip}.` : `${saved}.`
}

/** 'YYYY-MM-DD' as "Nov 1, 2026". */
export const shortDate = (d: string) =>
  new Date(`${d}T00:00:00Z`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' })

/** " · from Nov 1, 2026", " · until …", " · Nov 1, 2026 – Jan 31, 2027", or "". */
export function datesNote(r: { effectiveFrom?: string | null; effectiveTo?: string | null }): string {
  if (r.effectiveFrom && r.effectiveTo) return ` · ${shortDate(r.effectiveFrom)} – ${shortDate(r.effectiveTo)}`
  if (r.effectiveFrom) return ` · from ${shortDate(r.effectiveFrom)}`
  if (r.effectiveTo) return ` · until ${shortDate(r.effectiveTo)}`
  return ''
}

/**
 * Shayna's 2026-10-05 rules (Teams), offered on the page as one-click suggestions and
 * never pre-loaded. "Private HMO Coins" goes first, so it wins over the HMO payer types.
 */
export const SUGGESTED_BILLER: { id: string; title: string; why: string; first: boolean; rules: BillerSentence[] }[] = [
  {
    id: 'private-hmo-coins',
    title: 'Private HMO Coins → HMO',
    why: 'HMO gets all the coinsurance when the payer is “Private HMO Coins”.',
    first: true,
    rules: [{ matchField: 'payer', matchValue: 'Private HMO Coins', creditedDepartment: 'HMO' }],
  },
  {
    id: 'hmo-private-pay-medicare',
    title: 'HMO and Private Pay payers → Medicare',
    why: 'Medicare gets the coinsurance when the payer is listed as Private Pay or as an HMO payer (e.g. HMO/Private Insurance).',
    first: false,
    rules: [
      { matchField: 'payer_type', matchValue: 'HMO*', creditedDepartment: 'Medicare' },
      { matchField: 'payer_type', matchValue: 'Private Pay', creditedDepartment: 'Medicare' },
    ],
  },
  {
    id: 'medicaid-by-primary',
    title: 'Medicaid payer types → the primary payer’s department',
    why: 'Coinsurance whose payer type contains “Medicaid” is credited by its primary payer (Yehuda, 2026-10-06). The model applies it.',
    first: true,
    rules: [{ matchField: 'payer_type', matchValue: '*Medicaid*', creditedDepartment: PRIMARY_PAYER }],
  },
]

/** An active rule already says this (same field, value and department, ignoring case). */
export const hasBillerRule = (rules: readonly BillerRule[], s: BillerSentence) =>
  rules.some(
    (r) =>
      r.active &&
      r.matchField === s.matchField &&
      norm(r.matchValue) === norm(s.matchValue) &&
      norm(r.creditedDepartment) === norm(s.creditedDepartment),
  )
